import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  /** @type {((event: Event) => void) | undefined} */
  #quantityUpdateHandler;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    if (this.dataset.rumuLineTotal === 'true') {
      const form = this.closest('product-form-component');
      if (form) {
        this.#quantityUpdateHandler = this.#handleQuantityUpdate.bind(this);
        form.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#quantityUpdateHandler);
        this.#updateLineTotal(this.#getFormQuantity());
      }
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    if (this.#quantityUpdateHandler) {
      const form = this.closest('product-form-component');
      form?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#quantityUpdateHandler);
    }
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    if (this.dataset.rumuLineTotal === 'true') {
      const newLineTotalPrice = event.detail.data.html.querySelector('product-price[data-rumu-line-total]');
      if (newLineTotalPrice) {
        this.dataset.unitPrice = newLineTotalPrice.dataset.unitPrice || this.dataset.unitPrice || '';
        this.dataset.compareAtUnitPrice =
          newLineTotalPrice.dataset.compareAtUnitPrice || this.dataset.compareAtUnitPrice || '0';
      } else if (event.detail.resource?.price != null) {
        this.dataset.unitPrice = String(event.detail.resource.price);
        if (event.detail.resource.compare_at_price != null) {
          this.dataset.compareAtUnitPrice = String(event.detail.resource.compare_at_price);
        }
      }

      this.#updateLineTotal(this.#getFormQuantity());
    }
  };

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate(event) {
    if (!(event.target instanceof Node) || !this.closest('product-form-component')?.contains(event.target)) {
      return;
    }

    const quantity = event.detail?.quantity;
    if (!quantity || quantity < 1) return;

    this.#updateLineTotal(quantity);
  }

  /**
   * @param {number} quantity
   */
  #updateLineTotal(quantity) {
    const unitPrice = Number(this.dataset.unitPrice);
    if (!unitPrice) return;

    const compareAtUnitPrice = Number(this.dataset.compareAtUnitPrice || 0);
    const showComparePrice = compareAtUnitPrice > unitPrice;
    const totalPrice = unitPrice * quantity;
    const totalCompareAtPrice = showComparePrice ? compareAtUnitPrice * quantity : 0;

    const priceElement = this.querySelector('.price');
    const compareAtElement = this.querySelector('.compare-at-price');

    if (priceElement) {
      priceElement.textContent = this.#formatCents(totalPrice);
    }

    if (compareAtElement && showComparePrice) {
      compareAtElement.textContent = this.#formatCents(totalCompareAtPrice);
    }
  }

  /**
   * @returns {number}
   */
  #getFormQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const quantity = Number.parseInt(quantityInput?.value || '1', 10);

    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  /**
   * @returns {string}
   */
  #getMoneyTemplate() {
    const form = this.closest('product-form-component');
    const templateEl = form?.querySelector('[data-rumu-money-format]');

    if (templateEl instanceof HTMLTemplateElement) {
      return templateEl.content.textContent?.trim() || '{{amount}}';
    }

    return templateEl?.textContent?.trim() || '{{amount}}';
  }

  /**
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatCents(moneyValue) {
    if (this.dataset.rumuLineTotal === 'true') {
      return this.#formatLineTotalCents(moneyValue);
    }

    const template = this.#getMoneyTemplate();
    const currencySymbol = this.dataset.currencySymbol || '';

    const formatted = template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';

      if (placeholder === 'currency') {
        return currencySymbol;
      }

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCentsValue(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });

    if (currencySymbol && !formatted.includes(currencySymbol)) {
      return `${currencySymbol}${formatted}`;
    }

    return formatted;
  }

  /**
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatLineTotalCents(moneyValue) {
    const currencySymbol = this.dataset.currencySymbol || '€';
    const amount = this.#formatCentsValue(moneyValue, '.', ',', DEFAULT_CURRENCY_DECIMALS);

    return `${amount}${currencySymbol}`;
  }

  /**
   * @param {number} moneyValue
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} precision
   * @returns {string}
   */
  #formatCentsValue(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [whole, fraction = ''] = roundedNumber.split('.');
    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
